import { useEffect, useSyncExternalStore } from "react";
import { store } from "../reducer";
import { ActionType } from "../actionTypes";
import { useAuthToken } from "../hook";
import { AGENT_HTTP } from "../api/agentUrl";
import type { ModelProviderOverride } from "../slices/ui";
import {
  LlmAccountSummary,
  LlmLoginSummary,
  ModelProvider,
  clearPreferredModel,
  deleteLlmLogin,
  readLlmAccount,
  readPreferredModel,
  renderLoginState,
  writeLlmAccount,
  writeLlmLogin,
  writePreferredModel,
} from "./modelProvider";

// ─── The one provider/model state both Settings and the chat read ─────────────
// Provider is the ACCOUNT setting (system:#llm-account). The model is the account's Preferred Model (system:#model), the
// ONE model setting, shared with the CLI and the MCP (pages/frontend/ui-patterns/model-setting.html); state.ui.selectedModel
// is only this tab's in-memory copy of it and is never stored in the browser. Both sides of the app render from this module:
// Settings writes the Preferred Model (or deletes it: Default) and the account row, the chat picker reads the same two
// sources. There is no second copy to drift, and no "Account setting" pseudo-provider any more.

export type ProviderKey = ModelProvider;

export interface ModelOption {
  id: string;
  name: string;
}

// Claude Code's own model aliases: `claude --model sonnet|opus|haiku`. The turn service passes the
// id straight to the child, so every id here is a value the CLI really accepts.
export const CLAUDE_MODELS: ModelOption[] = [
  { id: "sonnet", name: "Claude Sonnet" },
  { id: "opus", name: "Claude Opus" },
  { id: "haiku", name: "Claude Haiku" },
];

export const CLAUDE_DEFAULT_MODEL = "sonnet";

// Used only until OpenRouter's catalog arrives (or when it cannot be read).
export const OPENROUTER_DEFAULT_MODEL = "deepseek/deepseek-v4-flash";

export const isClaudeModel = (id: string | undefined): boolean =>
  !!id && (CLAUDE_MODELS.some((m) => m.id === id) || /^claude-/.test(id));

export const modelsForProvider = (
  provider: ProviderKey,
  openrouter: ModelOption[],
): ModelOption[] => (provider === "anthropic" ? CLAUDE_MODELS : openrouter);

// The documented default when the catalog lists it (the catalog is newest-first, so its first entry is not a default);
// the first catalog entry only when the documented default is gone from it.
export const defaultModelFor = (provider: ProviderKey, openrouter: ModelOption[]): string =>
  provider === "anthropic"
    ? CLAUDE_DEFAULT_MODEL
    : openrouter.some((m) => m.id === OPENROUTER_DEFAULT_MODEL) || openrouter.length === 0
      ? OPENROUTER_DEFAULT_MODEL
      : openrouter[0].id;

export const modelName = (models: ModelOption[], id: string): string =>
  models.find((m) => m.id === id)?.name ?? id;

// The provider a chat runs on: this tab's chat-level override (the refusal banner's switch) when there is one, else the
// Account row's. An Advanced account key shows ANOTHER account, so this chat's override says nothing about it.
export const effectiveProviderOf = (
  accountProvider: ProviderKey,
  override: ProviderKey | null,
  accountKey: string,
): ProviderKey => (!accountKey && override ? override : accountProvider);

// The model a chat on `provider` uses, for the pickers and for session.create alike: the pick when it is one of the
// provider's models; while OpenRouter's catalog has not arrived (an empty list is no evidence the pick is wrong) the pick,
// unless it is a Claude model; otherwise the provider's default.
export const resolveModel = (provider: ProviderKey, catalog: ModelOption[], picked: string): string => {
  const list = modelsForProvider(provider, catalog);
  if (list.some((m) => m.id === picked)) return picked;
  if (list.length === 0 && provider === "openrouter" && !isClaudeModel(picked)) return picked;
  return defaultModelFor(provider, catalog);
};

// ─── Plain-words status ──────────────────────────────────────────────────────

export interface ProviderStatus {
  active: string;
  claude: string;
  claudeState: "none" | "valid" | "expiring" | "expired";
  expires: string;
}

export const providerStatus = (
  provider: ProviderKey,
  login: LlmLoginSummary,
  now = Date.now(),
): ProviderStatus => {
  const view = renderLoginState(login, now);
  const expires = view.expires;
  const claude =
    view.state === "none"
      ? "Not connected"
      : view.state === "expired"
        ? `Expired on ${expires}`
        : view.state === "expiring"
          ? `Connected — expires soon, ${expires}`
          : `Connected — expires ${expires}`;
  return {
    active: provider === "anthropic" ? "Your Claude subscription" : "OpenRouter",
    claude,
    claudeState: view.state,
    expires,
  };
};

// ─── Store ───────────────────────────────────────────────────────────────────

export interface AgentProviderState {
  loaded: boolean;
  readFailed: boolean;
  error: string;
  account: LlmAccountSummary | null;
  accountKey: string;
  // The stored Preferred Model of the shown account; "" means no row (Default, inherit).
  preferredModel: string;
  openrouter: ModelOption[];
  openrouterLoaded: boolean;
}

const EMPTY: AgentProviderState = {
  loaded: false,
  readFailed: false,
  error: "",
  account: null,
  accountKey: "",
  preferredModel: "",
  openrouter: [],
  openrouterLoaded: false,
};

let snapshot: AgentProviderState = EMPTY;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const update = (patch: Partial<AgentProviderState>) => {
  snapshot = { ...snapshot, ...patch };
  emit();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const getSnapshot = () => snapshot;

let accountRead: Promise<void> | null = null;
let modelsRead = false;

// A read generation (the Settings dialog's flowGenRef, ModelProviderSettings.tsx, is the same idea): every read, an
// Advanced account-key change and a reset bump it, so a read or a provider write that resolves after a newer one started,
// or after a sign-out, cannot write its account into the snapshot.
let readGen = 0;

const readOpenrouterModels = async (): Promise<void> => {
  try {
    const res = await fetch(`${AGENT_HTTP}/models`);
    const body = await res.json();
    const raw = Array.isArray(body) ? body : body?.data;
    const list: ModelOption[] = (Array.isArray(raw) ? raw : [])
      .filter((m: any) => m && typeof m.id === "string" && m.id)
      .map((m: any) => ({ id: m.id, name: typeof m.name === "string" && m.name ? m.name : m.id }));
    update({ openrouter: list, openrouterLoaded: true });
  } catch {
    update({ openrouterLoaded: true });
  }
};

const readAccount = async (accountKey: string): Promise<void> => {
  const gen = ++readGen;
  try {
    const [account, preferredModel] = await Promise.all([
      readLlmAccount(accountKey || undefined),
      readPreferredModel(accountKey || undefined),
    ]);
    if (gen !== readGen) return;
    update({ account, preferredModel, loaded: true, readFailed: false, error: "" });
    // The signed-in account's chat follows the server: its stored model, else the provider's default.
    if (!accountKey) setSelectedModel(preferredModel || defaultModelFor(account["model-provider"], snapshot.openrouter));
  } catch (e: any) {
    if (gen !== readGen) return;
    // A failed read must not close the one-shot guard: keep it clear so the next auth change or
    // an explicit reload can try again instead of being blocked for the life of the module.
    accountRead = null;
    update({ loaded: true, readFailed: true, error: e?.message ?? "account read failed" });
  }
};

// Sign-out/sign-in of a different account clears the module's one-shot reads. Live subscribers
// stay subscribed: resetAgentProvider is called while Settings and the chat are still mounted.
export const resetAgentProvider = (): void => {
  readGen += 1;
  snapshot = EMPTY;
  accountRead = null;
  modelsRead = false;
  store.dispatch({ type: ActionType.SET_SELECTED_PROVIDER, provider: null });
  emit();
};

export const reloadAgentProvider = async (): Promise<void> => {
  accountRead = readAccount(snapshot.accountKey);
  await accountRead;
};

// The provider the account row names; the row itself is already normalized to openrouter|anthropic.
export const activeProvider = (state: AgentProviderState): ProviderKey =>
  state.account?.["model-provider"] ?? "openrouter";

// The model session.create sends (AiConnectionProvider sendCreate): resolveModel over the same inputs the pickers render
// from, so the frame never names a model the picker does not show. Before the row is read, after a failed read, or while
// an Advanced account key shows another account, nothing is known about this chat's provider: the stored pick goes as is.
export const chatModel = (): string => {
  const ui = store.getState().ui;
  if (!snapshot.loaded || snapshot.readFailed || snapshot.accountKey) return ui.selectedModel;
  if (snapshot.preferredModel && !ui.selectedProvider) return snapshot.preferredModel;
  const provider = effectiveProviderOf(activeProvider(snapshot), ui.selectedProvider, "");
  return resolveModel(provider, snapshot.openrouter, ui.selectedModel);
};

const setSelectedModel = (model: string) => {
  store.dispatch({ type: ActionType.SET_SELECTED_MODEL, model });
};

const setSelectedProvider = (provider: ModelProviderOverride) => {
  store.dispatch({ type: ActionType.SET_SELECTED_PROVIDER, provider });
};

export interface AgentProvider {
  provider: ProviderKey;
  // The Account row's provider. `provider` is the effective one (the chat override, else this), and is what every
  // control renders; Settings shows this one only to say a chat override is in place.
  accountProvider: ProviderKey;
  account: LlmAccountSummary | null;
  loaded: boolean;
  readFailed: boolean;
  error: string;
  status: ProviderStatus;
  models: ModelOption[];
  selectedModel: string;
  modelLabel: string;
  accountKey: string;
  chooseProvider: (provider: ProviderKey) => Promise<void>;
  chooseModel: (model: string) => Promise<void>;
  // Default: delete the stored Preferred Model (the account inherits). usesDefaultModel is true when there is none.
  chooseDefaultModel: () => Promise<void>;
  usesDefaultModel: boolean;
  defaultModelLabel: string;
  // A chat-level swap that does not change the Account setting (the refusal banner's action).
  switchChatProvider: (provider: ProviderKey) => void;
  disconnectClaude: () => Promise<void>;
  storeLogin: (kind: "oauth-token" | "api-key", secret: string) => Promise<void>;
  setAccountKey: (key: string) => void;
  reload: () => Promise<void>;
}

export function useAgentProvider(): AgentProvider {
  const [authToken] = useAuthToken();
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (!authToken || accountRead) return;
    accountRead = readAccount(state.accountKey);
  }, [authToken, state.accountKey]);

  useEffect(() => {
    if (modelsRead) return;
    modelsRead = true;
    void readOpenrouterModels();
  }, []);

  const accountProvider = activeProvider(state);
  const selectedModel = useSyncExternalStore(
    (listener) => store.subscribe(listener),
    () => store.getState().ui.selectedModel,
    () => store.getState().ui.selectedModel,
  );
  const selectedProvider = useSyncExternalStore(
    (listener) => store.subscribe(listener),
    () => store.getState().ui.selectedProvider,
    () => store.getState().ui.selectedProvider,
  );
  // ONE provider for everything this hook renders (the picker's pressed button, the model list, the selected model, the
  // status line); session.create reads the same rule through chatModel().
  const provider = effectiveProviderOf(accountProvider, selectedProvider, state.accountKey);
  const models = modelsForProvider(provider, state.openrouter);
  // A stored Preferred Model is shown verbatim (one value: what the CLI and Cas read is what this picker shows); only the
  // absence of a row falls back to the provider's default. The Advanced account view shows THAT account's model.
  const effectiveModel = state.accountKey
    ? state.preferredModel || defaultModelFor(provider, state.openrouter)
    : state.preferredModel && !selectedProvider
      ? state.preferredModel
      : resolveModel(provider, state.openrouter, selectedModel);

  // The Account setting is DISPLAY-only: it never becomes this tab's session.create choice (ui.selectedProvider stays
  // null unless the refusal banner set it), so a second tab's change of the Account is not overridden by this tab. The
  // stored model follows the effective provider; an Advanced account key never moves this chat's model.
  useEffect(() => {
    if (!state.loaded || state.readFailed || state.accountKey) return;
    if (effectiveModel !== selectedModel) setSelectedModel(effectiveModel);
  }, [state.loaded, state.readFailed, state.accountKey, effectiveModel, selectedModel]);

  // Writes the Account row and nothing of the chat's own: the chat follows the Account setting (a chat-level override,
  // the refusal banner's, is cleared here, never set), and an Advanced account key's row never touches this chat.
  const chooseProvider = async (next: ProviderKey): Promise<void> => {
    const gen = readGen;
    await writeLlmAccount(state.accountKey || undefined, { "model-provider": next });
    if (gen !== readGen) return;   // a newer read, an account-key change or a sign-out superseded this view
    update({ account: snapshot.account ? { ...snapshot.account, "model-provider": next } : null });
    if (!snapshot.accountKey) {
      setSelectedProvider(null);
      setSelectedModel(resolveModel(next, snapshot.openrouter, store.getState().ui.selectedModel));
      // A stored model the new provider cannot run is deleted, so no mismatch is stored (and no Claude alias is
      // inherited by a service account that has no login).
      if (snapshot.preferredModel && isClaudeModel(snapshot.preferredModel) !== (next === "anthropic")) {
        await clearPreferredModel();
        if (gen !== readGen) return;
      }
    }
    await reloadAgentProvider();
  };

  // The pick IS the Preferred Model: write it for the shown account (the signed-in one, or the Advanced account key), then
  // read the server back, so the picker shows what the CLI and Cas will read. A failed write keeps the old value.
  const chooseModel = async (model: string): Promise<void> => {
    const gen = readGen;
    try {
      await writePreferredModel(model, state.accountKey || undefined);
    } catch (e: any) {
      if (gen === readGen) update({ error: e?.message ?? "model write failed" });
      return;
    }
    if (gen !== readGen) return;
    await reloadAgentProvider();
  };

  // Default: delete the row (the same delete as `qo agent model --clear`), then read back; the account inherits.
  const chooseDefaultModel = async (): Promise<void> => {
    const gen = readGen;
    try {
      await clearPreferredModel(state.accountKey || undefined);
    } catch (e: any) {
      if (gen === readGen) update({ error: e?.message ?? "model clear failed" });
      return;
    }
    if (gen !== readGen) return;
    await reloadAgentProvider();
  };

  // The refusal banner's "Switch this chat to OpenRouter": a chat-level override, and the model moved into that
  // provider's list. It does not write the Account setting. (The new server session it needs is ProviderRefusal's onRestart.)
  const switchChatProvider = (next: ProviderKey) => {
    setSelectedProvider(next);
    setSelectedModel(resolveModel(next, snapshot.openrouter, store.getState().ui.selectedModel));
  };

  const disconnectClaude = async (): Promise<void> => {
    await deleteLlmLogin(state.accountKey || undefined);
    await reloadAgentProvider();
  };

  const storeLogin = async (kind: "oauth-token" | "api-key", secret: string): Promise<void> => {
    await writeLlmLogin(state.accountKey || undefined, kind, secret);
    await reloadAgentProvider();
  };

  const setAccountKey = (key: string) => {
    accountRead = null;
    readGen += 1;
    update({ account: null, accountKey: key, loaded: false, readFailed: false, error: "" });
  };

  const status = providerStatus(provider, state.account?.login ?? { present: false });

  return {
    provider,
    accountProvider,
    account: state.account,
    loaded: state.loaded,
    readFailed: state.readFailed,
    error: state.error,
    status,
    models,
    selectedModel: effectiveModel,
    modelLabel: modelName(models, effectiveModel),
    accountKey: state.accountKey,
    chooseProvider,
    chooseModel,
    chooseDefaultModel,
    usesDefaultModel: state.preferredModel === "",
    defaultModelLabel: modelName(models, defaultModelFor(provider, state.openrouter)),
    switchChatProvider,
    disconnectClaude,
    storeLogin,
    setAccountKey,
    reload: reloadAgentProvider,
  };
}
